'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { type FC, Fragment } from 'react';

import Icon from 'molecules/Icon';
import MobilePopUpMenu from 'molecules/MobilePopup';

import Pagination from 'components/ListingDeck/Pagination';
import Sidebar from 'components/ListingDeck/Sidebar';
import styles from 'components/ListingDeck/listingDeck.module.scss';

import { isNonEmptyArray } from 'utils/functions';

import type { SideBarItem } from 'components/ListingDeck/Sidebar';
import type { TemplateModelData } from 'types/types';

const LearningCard = dynamic(() => import('components/ListingDeck/LearningCard'));

export const getStringParam = (param: string | undefined): string | null => (typeof param === 'string' ? param : null);

export const getArrayParam = (param: string | undefined): string[] | null =>
  typeof param === 'string' ? param.split(',') : null;

export interface ListingDeckProps {
  page: number;
  sidebarItems: SideBarItem[];
  loading: boolean;
  orderedFilteredItems: TemplateModelData[];
  search?: string;
}

const ListingDeck: FC<ListingDeckProps> = ({ page, sidebarItems, loading, orderedFilteredItems, search = '' }) => {
  const pageSize = 12;
  const start = (page - 1) * pageSize;

  const postsToShow = orderedFilteredItems.slice(start, start + pageSize);
  const postsExist = isNonEmptyArray(orderedFilteredItems);
  const idToScrollTo = 'listing-results';

  return (
    <section className={styles.twoColumn}>
      <div id="desktop-sidebar" className={styles.desktopSidebar}>
        <div className={styles.sidebar}>
          <label htmlFor="sidebarContent">
            <span className="visuallyHidden">sidebarContent</span>
          </label>
        </div>
        <Sidebar items={sidebarItems} />
      </div>
      <div id="mobile-sidebar" className={styles.mobileSidebar}>
        <MobilePopUpMenu
          openImage={<Icon icon="filters" />}
          openText="Filter"
          closeImage={<Icon icon="icon-cross" />}
          closeText={<span className="visuallyHidden">Close</span>}
          panelHeading="Filter Results"
        >
          <Sidebar items={sidebarItems} />
        </MobilePopUpMenu>
      </div>
      <div id="listing-results" className={styles.results}>
        {loading && <Image src="/loading_icon.gif" alt="Loading..." width={150} height={150} />}
        {!loading && !postsExist && <p className={styles.notFound}>No results found for {search}</p>}
        <div className={styles.cardWrapper}>
          {postsExist && (
            <Fragment>
              {postsToShow.map(entry => {
                const url = entry?.data?.url || '';
                const title = entry?.data?.templateInfo?.title || '';

                return <LearningCard title={title} key={entry.id} modelId={entry.modelId || ''} link={url} />;
              })}
            </Fragment>
          )}
        </div>
        {postsExist && (
          <Pagination
            totalItems={orderedFilteredItems.length}
            limit={pageSize}
            currentPage={page}
            idToScrollTo={idToScrollTo}
          />
        )}
      </div>
    </section>
  );
};

export default ListingDeck;
